import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Small shared pieces for the admin screens.
 *
 * Kept in one file because each is a dozen lines and they are only used by
 * the three admin pages — splitting them further would be more imports than
 * code.
 */

/* ----------------------------- page header ---------------------------- */

export function PageHeader({ title, count, children }) {
  return (
    <div className="admin-header">
      <div>
        <h1 className="admin-title">
          {title}
          {count !== undefined && <span className="admin-count">{count}</span>}
        </h1>
      </div>
      <div className="admin-actions">{children}</div>
    </div>
  );
}

/* ------------------------------- search ------------------------------- */

export function SearchBox({ value, onChange, placeholder = 'Search…', autoFocus }) {
  return (
    <div className="admin-search">
      <i className="bi bi-search" aria-hidden="true" />
      <input
        type="search"
        className="form-control form-control-sm"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={e => onChange(e.target.value)}
      />
      {value && (
        <button type="button" className="admin-search-clear"
                onClick={() => onChange('')} aria-label="Clear search">
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

/* ---------------------------- filter chips ---------------------------- */

export function FilterChips({ options, value, onChange }) {
  return (
    <div className="admin-chips" role="group" aria-label="Filter">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`admin-chip${value === opt.value ? ' is-active' : ''}`}
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
          {opt.count !== undefined && <span className="admin-chip-count">{opt.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------ sorting ------------------------------- */

/**
 * Sort state plus a comparator. `accessor` maps a row to a sortable value;
 * nulls always sort last regardless of direction, because a blank cell
 * floating to the top of an ascending sort is never what you want.
 */
export function useSort(initialKey, initialDir = 'asc') {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });

  const toggle = (key) => setSort(s =>
    s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });

  const sortRows = (rows, accessors) => {
    const accessor = accessors[sort.key];
    if (!accessor) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      const aEmpty = av === null || av === undefined;
      const bEmpty = bv === null || bv === undefined;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (typeof av === 'string' && typeof bv === 'string') {
        return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * dir;
      }
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
  };

  return { sort, toggle, sortRows };
}

export function SortHeader({ label, sortKey, sort, onToggle, className, style }) {
  const active = sort.key === sortKey;
  return (
    <th className={className} style={style}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="admin-sort" onClick={() => onToggle(sortKey)}>
        {label}
        <i className={`bi ${active
          ? (sort.dir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill')
          : 'bi-caret-up'} admin-sort-icon${active ? ' is-active' : ''}`} />
      </button>
    </th>
  );
}

/* ---------------------------- empty states ---------------------------- */

export function EmptyState({ icon = 'bi-inbox', title, children, action }) {
  return (
    <div className="admin-empty">
      <i className={`bi ${icon}`} aria-hidden="true" />
      <div className="admin-empty-title">{title}</div>
      {children && <div className="admin-empty-body">{children}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/* --------------------------- confirm dialog ---------------------------
   Replaces window.confirm(), which cannot be styled, cannot say anything
   beyond one line of plain text, and on destructive actions gives no room
   to explain the consequence (e.g. that deleting a user also removes their
   directory account).
   --------------------------------------------------------------------- */

export function ConfirmDialog({ title, body, confirmLabel = 'Confirm',
                                variant = 'danger', busy, onConfirm, onCancel }) {
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  return (
    <div className="modal d-block admin-modal" role="dialog" aria-modal="true">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title h6 mb-0">{title}</h2>
          </div>
          <div className="modal-body">{body}</div>
          <div className="modal-footer">
            <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="button" ref={ref} className={`btn btn-sm btn-${variant}`}
                    onClick={onConfirm} disabled={busy}>
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- modal -------------------------------- */

export function Modal({ title, size = 'lg', onClose, children, footer }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal d-block admin-modal" role="dialog" aria-modal="true">
      <div className={`modal-dialog modal-${size} modal-dialog-scrollable`}>
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title h6 mb-0">{title}</h2>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          {children}
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

/** Labelled section inside a dialog, so long forms read as groups. */
export function FormSection({ title, hint, children }) {
  return (
    <section className="form-section">
      <div className="form-section-head">
        <h3 className="form-section-title">{title}</h3>
        {hint && <p className="form-section-hint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/* ------------------------------ helpers ------------------------------- */

/** Case-insensitive "does any of these fields contain the term" filter. */
export function useTextFilter(rows, term, fields) {
  return useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(row =>
      fields.some(f => String(row[f] ?? '').toLowerCase().includes(q)));
  }, [rows, term, fields]);
}

/**
 * Date and time down to the minute, with "Today"/"Yesterday" for recent
 * entries because those are the ones an admin is usually checking.
 *
 * Timestamps are stored as UTC (SYSUTCDATETIME) and the mssql driver hands
 * back a correctly-offset Date, so rendering in the browser's local zone is
 * right. The title attribute carries the full value including seconds and
 * zone, for when the exact moment matters.
 */
export function formatDateTime(value, { fallback = 'never' } = {}) {
  if (!value) return { text: fallback, title: undefined, muted: true };

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { text: fallback, title: undefined, muted: true };

  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const title = date.toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'long' });

  // Compare calendar days, not elapsed hours: 30 hours ago is "yesterday",
  // but elapsed-time arithmetic would round it to 1 day and call it today.
  const startOfDay = (d) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };
  const now = new Date();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

  // dayDiff < 0 means a clock-skewed future timestamp; treat it as today.
  if (dayDiff <= 0) return { text: `Today ${time}`, title };
  if (dayDiff === 1) return { text: `Yesterday ${time}`, title };

  const opts = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
  if (date.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return { text: date.toLocaleString(undefined, opts), title };
}

/** Renders a timestamp cell using formatDateTime. */
export function DateTime({ value, fallback = 'never' }) {
  const { text, title, muted } = formatDateTime(value, { fallback });
  return <span className={muted ? 'admin-muted' : undefined} title={title}>{text}</span>;
}

export function Badge({ tone = 'secondary', children, title }) {
  return <span className={`admin-badge tone-${tone}`} title={title}>{children}</span>;
}
